import { useEffect, useState } from "react";
import RestaurantCard from "../components/restaurants/RestaurantCard";
import { Skeleton } from "../components/ui/skeleton";
import { Helmet } from "react-helmet";
import { useLocation } from "wouter";
import { motion } from "framer-motion";
import { Restaurant } from "../types";

const Shuffle = () => {
    const [restaurants, setRestaurants] = useState<Restaurant[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [, setLocation] = useLocation();

    // Highest price logic
    const highestPrice = restaurants && restaurants.length > 0
        ? Math.max(...restaurants.map(r => Number(r.price) || 0))
        : null;

    useEffect(() => {
        const result = localStorage.getItem("shuffleResult");
        if (result) setRestaurants(JSON.parse(result));
        setLoading(false);
    }, []);

    const handleConfirm = () => {
        setLocation("/booking-shuffle");
    };

    // --- MODIFIED Skeletons for Compact Height ---
    const renderSkeletons = () => (
        Array(3)
            .fill(0)
            .map((_, index) => (
                <div key={index} className="overflow-hidden rounded-xl bg-white shadow-md dark:bg-gray-800">
                    <Skeleton className="h-32 w-full" /> {/* h-48 → h-32 */}
                    <div className="p-3 space-y-2"> {/* p-6 → p-3, space-y-4 → space-y-2 */}
                        <div className="flex justify-between">
                            <Skeleton className="h-6 w-1/3" />
                            <Skeleton className="h-6 w-16" />
                        </div>
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                        <div className="flex justify-between">
                            <Skeleton className="h-6 w-16" />
                            <Skeleton className="h-10 w-32" />
                        </div>
                    </div>
                </div>
            ))
    );

    return (
        <>
            <Helmet>
                <title>Your Surprise Restaurants | SurpriseTable</title>
                <meta
                    name="description"
                    content="These are your surprise dining picks. Enjoy your meal!"
                />
            </Helmet>

            {/* py-16 → py-6 */}
            <section className="py-6 transition-colors duration-200 bg-background dark:bg-background min-h-screen">
                <div className="container mx-auto px-2 sm:px-4 lg:px-6">
                    {/* mb-8 → mb-4, text-3xl → text-2xl */}
                    <h2 className="mb-4 text-center text-2xl font-bold text-gray-800 dark:text-white">
                        🎉 Your Surprise Dining Picks
                    </h2>

                    {/* mb-8 → mb-3, p-6 → p-3 */}
                    <div className="mb-3 max-w-xl mx-auto text-center">
                        <div className="rounded-lg bg-primary/10 p-3 dark:bg-gray-900/60">
                            <p className="text-base text-primary font-semibold mb-1">
                                Based on your preferences, we've selected these 3 surprise restaurants for you! 🍽️
                            </p>
                        </div>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {renderSkeletons()}
                        </div>
                    ) : !restaurants || restaurants.length === 0 ? (
                        <div className="my-8 rounded-lg bg-gray-50 p-4 text-center dark:bg-gray-800">
                            <h3 className="mb-1 text-base font-semibold text-gray-800 dark:text-gray-200">
                                No restaurants found
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                Please start a new booking to get your surprise restaurants.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* gap-6 → gap-4, mb-10 → mb-3 */}
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-3">
                                {restaurants.map((restaurant) => (
                                    <RestaurantCard
                                        key={restaurant.id}
                                        restaurant={restaurant}
                                        showPrice={false}
                                        // (optional: pass a prop to make RestaurantCard more compact)
                                    />
                                ))}
                            </div>

                            {/* --- Booking Price Breakdown --- */}
                            <div className="flex flex-col items-center">
                                {/* mb-4 → mb-2, max-w-sm → max-w-xs, p-5 → p-3 */}
                                <div className="mb-2 w-full max-w-xs">
                                    <div className="rounded-xl border border-primary bg-white dark:bg-gray-900 p-3 shadow-sm text-center">
                                        <div className="text-lg font-bold text-gray-800 dark:text-white mb-1">
                                            Your Booking Price
                                        </div>
                                        <div className="text-2xl font-extrabold text-primary mb-1">
                                            {highestPrice ? `€${highestPrice.toFixed(2)} per person` : "-"}
                                        </div>
                                        <div className="text-gray-600 dark:text-gray-300 text-xs mb-1">
                                            Highest price among your 3 matches.
                                        </div>
                                        <span className="text-green-600 font-semibold text-xs">
                                            If your restaurant's price is lower, you'll be refunded automatically!
                                        </span>
                                        <div className="text-green-600 font-medium text-xs">
                                            Best price guarantee, every time.
                                        </div>
                                    </div>
                                </div>

                                {/* py-4 → py-2.5, mt-2 → mt-1 */}
                                <motion.button
                                    className="mt-1 px-8 py-2.5 text-base font-bold bg-primary text-white rounded-lg
                                    hover:bg-primary/90 transition-colors duration-150 shadow-lg focus:outline-none focus:ring-2
                                    focus:ring-primary/60 focus:ring-offset-2 ring-offset-white dark:ring-offset-gray-800"
                                    onClick={handleConfirm}
                                    autoFocus
                                    initial={{ scale: 0.98, opacity: 0 }}
                                    animate={{
                                        scale: [1, 1.07, 1],
                                        opacity: 1
                                    }}
                                    transition={{
                                        scale: { duration: 1.4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" },
                                        opacity: { duration: 0.3 }
                                    }}
                                    whileTap={{ scale: 0.97 }}
                                >
                                    Review Booking
                                    <motion.span
                                        className="ml-2 text-xl inline-block"
                                        initial={{ y: 0 }}
                                        animate={{ y: [0, -10, 0] }}
                                        transition={{
                                            repeat: Infinity,
                                            repeatDelay: 2.5,
                                            duration: 0.7,
                                            ease: "easeInOut",
                                            delay: 1,
                                        }}
                                    >
                                        👉
                                    </motion.span>
                                </motion.button>
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default Shuffle;
